import React, { useState } from 'react'
import {
  Package,
  ArrowRightLeft,
  Settings,
  ChevronDown,
  ChevronUp,
  Trash2,
  X,
  Building2
} from 'lucide-react'
import { cn } from '@/app/lib/utils'
import { Button } from './button'

// ─── Tipos ────────────────────────────────────────────────
export type NotificationType = 'transfer' | 'inventario' | 'sucursales' | 'system'

export interface NotificationData {
  id: string | number
  type: NotificationType
  title: string
  description: string
  isRead: boolean
  createdAt: string
  actionText?: string
  actionUrl?: string
}

interface NotificationStackProps {
  items: NotificationData[]
  type: NotificationType
  onActionClick?: (notification: NotificationData) => void
  onToggleRead?: (id: string | number) => void
  onDelete?: (id: string | number) => void
  onDeleteGroup?: (type: NotificationType) => void
}

// ───  Configuracion Visual ────────────────────────────
const presetsConfig = {
  transfer: {
    appName: 'TRANSFERENCIAS',
    icon: ArrowRightLeft,
    appColor: 'bg-purple-500',
  },
  inventario: {
    appName: 'INVENTARIO',
    icon: Package,
    appColor: 'bg-orange-500',
  },
  sucursales: {
    appName: 'SUCURSALES',
    icon: Building2,
    appColor: 'bg-blue-600',
  },
  system: {
    appName: 'SISTEMA',
    icon: Settings,
    appColor: 'bg-slate-500',
  }
}

// ─── Componente Principal  ──────────────────────────────────
export function NotificationStack({ items, type, onActionClick, onToggleRead, onDelete, onDeleteGroup }: NotificationStackProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!items || items.length === 0) return null

  const preset = presetsConfig[type] || presetsConfig.system
  const IconComponent = preset.icon
  const isStacked = items.length > 1
  const latestItem = items[0] // el primero es el mas reciente

  // ───  Sub-Componentes UI ──────────────────────────────────────────────

  // Contenido individual de la notificación
  const NotificationContent = ({ item, isTop = false }: { item: NotificationData, isTop?: boolean }) => (
    <div className={cn(
      "p-3.5 transition-colors relative group/item",
      !item.isRead ? "bg-white/60" : "bg-transparent",
      !isTop && "border-t border-slate-200/50" // Separador si es parte de una lista expandida
    )}>
      <div className="flex items-start justify-between gap-3 pr-2">
        <div className="flex-1 min-w-0">
          <h4 className={cn("text-[13px] font-bold leading-tight", !item.isRead ? "text-slate-900 pr-3" : "text-slate-700")}>
            {item.title}
          </h4>
          <p className="mt-1 text-xs text-slate-500 line-clamp-2 leading-relaxed">
            {item.description}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[10px] font-medium text-slate-400">
              {item.createdAt}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end justify-start gap-2 shrink-0">
          <div className="flex items-center gap-2 h-6 -mt-1">
            {onToggleRead && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleRead(item.id); }}
                className="text-[10px] font-semibold text-slate-400 hover:text-slate-600 transition-colors opacity-0 group-hover/item:opacity-100"
              >
                {item.isRead ? "Marcar no leída" : "Marcar leída"}
              </button>
            )}

            <div className="relative flex items-center justify-center w-6 h-6 -mr-1">
              {!item.isRead && (
                <div className="absolute h-2 w-2 rounded-full bg-blue-500 shadow-sm shadow-blue-200 transition-opacity duration-200 group-hover/item:opacity-0" />
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onDelete && onDelete(item.id); }}
                className="absolute p-1 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all opacity-0 group-hover/item:opacity-100"
                title="Eliminar notificación"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {item.actionText && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2.5 text-[10px] font-bold rounded-md bg-white border-slate-200 shadow-sm hover:bg-slate-50"
              onClick={(e) => { e.stopPropagation(); onActionClick && onActionClick(item); }}
            >
              {item.actionText}
            </Button>
          )}
        </div>
      </div>
    </div>
  )

  const CardHeader = () => (
    <div className="flex items-center justify-between px-3 py-2 bg-white/40 border-b border-slate-100/50 backdrop-blur-md">
      <div className="flex items-center gap-2">
        <div className={cn("flex h-5 w-5 items-center justify-center rounded-[6px] shadow-sm", preset.appColor)}>
          <IconComponent className="h-3 w-3 text-white" />
        </div>
        <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">
          {preset.appName}
        </span>
      </div>

      <div className="flex items-center gap-3">
        {isStacked && (
          <span className="text-[10px] font-medium text-slate-400">
            {items.length} notificaciones
          </span>
        )}

        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onDeleteGroup && onDeleteGroup(type); }}
            className="p-1 rounded text-slate-400 hover:bg-slate-200/50 transition-colors"
            title="Limpiar grupo"
          >
            <X className="h-3 w-3" />
          </button>

          {isStacked && (
            <button
              onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
              className="p-1 rounded hover:bg-slate-200/50 transition-colors text-slate-400"
            >
              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="relative isolate group">
      {/* Efecto de apilamiento cuando hay muchos */}
      {isStacked && !isExpanded && (
        <div className="absolute inset-x-3 -top-2 h-full rounded-[14px] bg-slate-100/80 border border-slate-200/40 shadow-sm -z-10 transition-all duration-300 group-hover:-top-2.5" />
      )}

      <div className={cn(
        "relative rounded-[14px] overflow-hidden transition-all duration-300",
        "bg-slate-50/80 backdrop-blur-xl border border-slate-200/60 shadow-sm hover:shadow-md",
        !latestItem.isRead && !isExpanded ? "ring-1 ring-blue-500/20" : ""
      )}>
        <CardHeader />

        <div className="flex flex-col">
          {/* Si esta expandido mostramos todas si no, solo la mas reciente */}
          {isExpanded ? (
            items.map((item, index) => (
              <NotificationContent key={item.id} item={item} isTop={index === 0} />
            ))
          ) : (
            <NotificationContent item={latestItem} isTop={true} />
          )}
        </div>
      </div>
    </div>
  )
}
